import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from 'components/ui/Header';
import { CheckCircle, AlertCircle, Search } from 'lucide-react';
import { API_ENDPOINTS } from '../../utils/config';

const ManageRoles = () => {
  const [roles, setRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [editingRole, setEditingRole] = useState(null);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [editSelectedPermissions, setEditSelectedPermissions] = useState([]);
  const [isLoadingEditPermissions, setIsLoadingEditPermissions] = useState(false);
  const [pendingDeleteRole, setPendingDeleteRole] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewingRole, setViewingRole] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);
  const [isLoadingView, setIsLoadingView] = useState(false);
  const [permissionSearchCreate, setPermissionSearchCreate] = useState('');
  const [permissionSearchEdit, setPermissionSearchEdit] = useState('');
  const [permissionSearchView, setPermissionSearchView] = useState('');

  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    permissions: []
  });

  const navigate = useNavigate();

  const redirectIfNoAccess = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.message || err?.response?.data?.detail || '';

    // If backend says user has no access to this module, don't show the page at all.
    if (status === 403 || /access to this module/i.test(detail)) {
      // Go back to a safe default page. This also ensures the user cannot stay on this module via URL.
      navigate('/dashboard', { replace: true });
      return true;
    }
    return false;
  };

  const canSubmit = useMemo(() => {
    return formValues.name.trim().length > 0 && formValues.permissions.length > 0;
  }, [formValues]);

  // Filter permissions based on search query for create form
  const filteredPermissionsCreate = useMemo(() => {
    if (!permissionSearchCreate.trim()) return permissions;
    const searchLower = permissionSearchCreate.toLowerCase();
    return permissions.filter((perm) => {
      const id = perm?.id ?? perm?.permission_id ?? perm?.value;
      const label = perm?.name ?? perm?.label ?? perm?.codename ?? String(id);
      return label.toLowerCase().includes(searchLower) || String(id).includes(searchLower);
    });
  }, [permissions, permissionSearchCreate]);

  // Filter permissions based on search query for edit form
  const filteredPermissionsEdit = useMemo(() => {
    if (!permissionSearchEdit.trim()) return permissions;
    const searchLower = permissionSearchEdit.toLowerCase();
    return permissions.filter((perm) => {
      const id = perm?.id ?? perm?.permission_id ?? perm?.value;
      const label = perm?.name ?? perm?.label ?? perm?.codename ?? String(id);
      return label.toLowerCase().includes(searchLower) || String(id).includes(searchLower);
    });
  }, [permissions, permissionSearchEdit]);

  const fetchPermissions = async () => {
    setIsLoadingPermissions(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(API_ENDPOINTS.PERMISSIONS_LIST, {
        headers: {
          'Accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      // Normalize possible API shapes
      const raw = Array.isArray(data)
        ? data
        : (data?.results || data?.data || data?.permissions || []);
      setPermissions(raw);
    } catch (err) {
      if (redirectIfNoAccess(err)) {
        return;
      }
      setError(err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Failed to load permissions');
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  useEffect(() => {
    // Attempt initial load in case user navigates directly
    fetchPermissions();
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRoles = async () => {
    setIsLoadingRoles(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(API_ENDPOINTS.ROLES_LIST, {
        headers: {
          'Accept': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      const raw = Array.isArray(data)
        ? data
        : (data?.results || data?.data || data?.roles || []);
      const normalized = raw.map((r) => ({
        id: r?.id ?? r?.role_id ?? r?.uuid ?? r?.name,
        name: r?.name ?? r?.title ?? String(r?.id ?? ''),
        description: r?.description ?? r?.details ?? '',
        permissions: r?.permissions || r?.permission_ids || []
      }));
      setRoles(normalized);
    } catch (err) {
      if (redirectIfNoAccess(err)) {
        return;
      }
      setError(err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Failed to load roles');
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const fetchRoleDetails = async (roleId) => {
    if (!roleId) return null;
    const token = localStorage.getItem('token');
    const { data } = await axios.get(API_ENDPOINTS.ROLES_DETAIL(roleId), {
      headers: {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    return data;
  };

  const toggleCreate = () => {
    setIsCreating((prev) => {
      const newState = !prev;
      if (!newState) {
        // Reset form and search when closing
        setFormValues({ name: '', description: '', permissions: [] });
        setPermissionSearchCreate('');
      }
      return newState;
    });
    setError('');
    // Load permissions on open to ensure fresh data and valid auth
    if (!isCreating && permissions.length === 0 && !isLoadingPermissions) {
      fetchPermissions();
    }
  };

  const handleCheckboxToggle = (permissionId) => {
    setFormValues((prev) => {
      const alreadySelected = prev.permissions.includes(permissionId);
      return {
        ...prev,
        permissions: alreadySelected
          ? prev.permissions.filter((id) => id !== permissionId)
          : [...prev.permissions, permissionId]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError('');
    try {
      const payload = {
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        permissions: formValues.permissions
      };
      const { data } = await axios.post(
        API_ENDPOINTS.ROLES_CREATE,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      // Refresh roles from server to reflect latest
      await fetchRoles();

      // Reset form
      setFormValues({ name: '', description: '', permissions: [] });
      setIsCreating(false);
    } catch (err) {
      if (redirectIfNoAccess(err)) {
        return;
      }
      setError(err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Failed to create role');
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestDelete = (role) => {
    setPendingDeleteRole(role);
    setActionError('');
    setActionSuccess('');
  };

  const cancelDelete = () => {
    setPendingDeleteRole(null);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteRole?.id) return;
    setIsDeleting(true);
    setActionError('');
    setActionSuccess('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete(API_ENDPOINTS.ROLES_DELETE(pendingDeleteRole.id), {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      await fetchRoles();
      setPendingDeleteRole(null);
      setActionSuccess(`Role "${pendingDeleteRole?.name || ''}" deleted successfully.`);
      setTimeout(() => setActionSuccess(''), 2000);
    } catch (err) {
      if (redirectIfNoAccess(err)) {
        return;
      }
      setActionError(err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Failed to delete role');
    } finally {
      setIsDeleting(false);
    }
  };

  const openEdit = async (role) => {
    setEditingRole(role);
    setEditForm({ name: role?.name || '', description: role?.description || '' });
    setActionError('');
    // Ensure permissions are loaded for editing
    if (permissions.length === 0 && !isLoadingPermissions) {
      await fetchPermissions();
    }
    // Try fetching full role details to get accurate permissions
    try {
      const detailed = await fetchRoleDetails(role?.id);
      const detailIds = (detailed?.permissions || detailed?.permission_ids || [])
        .map((p) => (typeof p === 'object' ? (p?.id ?? p?.permission_id) : p))
        .filter((v) => v !== undefined && v !== null);
      if (detailIds.length > 0) {
        setEditSelectedPermissions(detailIds);
        return;
      }
    } catch (_) {
      // ignore and fallback
    }
    // Fallback: Preselect from list item if present
    const rolePermIds = (role?.permissions || role?.permission_ids || [])
      .map((p) => (typeof p === 'object' ? (p?.id ?? p?.permission_id) : p))
      .filter((v) => v !== undefined && v !== null);
    setEditSelectedPermissions(rolePermIds);
  };

  const closeEdit = () => {
    setEditingRole(null);
    setEditForm({ name: '', description: '' });
    setEditSelectedPermissions([]);
    setPermissionSearchEdit('');
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingRole?.id) return;
    setIsUpdatingRole(true);
    setActionError('');
    setActionSuccess('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        API_ENDPOINTS.ROLES_UPDATE(editingRole.id),
        { name: editForm.name, description: editForm.description, permissions: editSelectedPermissions },
        { headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      await fetchRoles();
      setActionSuccess('Role updated successfully');
      setTimeout(() => {
        closeEdit();
        setActionSuccess('');
      }, 1200);
    } catch (err) {
      if (redirectIfNoAccess(err)) {
        return;
      }
      setActionError(err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Failed to update role');
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const toggleEditPermission = (permissionId) => {
    setEditSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const openView = async (role) => {
    setViewingRole(role);
    setViewDetails(null);
    setIsLoadingView(true);
    try {
      if (permissions.length === 0 && !isLoadingPermissions) {
        await fetchPermissions();
      }
      const detailed = await fetchRoleDetails(role?.id);
      setViewDetails(detailed || role);
    } catch (_) {
      setViewDetails(role);
    } finally {
      setIsLoadingView(false);
    }
  };

  const closeView = () => {
    setViewingRole(null);
    setViewDetails(null);
    setPermissionSearchView('');
  };

  const renderViewPermissions = () => {
    const permList = (viewDetails?.permissions || viewingRole?.permissions || [])
      .map((p) => (typeof p === 'object' ? p : { id: p }))
      .filter((p) => p !== undefined && p !== null);
    if (!permList || permList.length === 0) {
      return <div className="text-sm text-muted-foreground px-2 py-2">No permissions</div>;
    }
    
    // Filter permissions based on search query for view
    let filteredPermList = permList;
    if (permissionSearchView.trim()) {
      const searchLower = permissionSearchView.toLowerCase();
      filteredPermList = permList.filter((p) => {
        const id = p?.id ?? p?.permission_id ?? p?.value;
        const matched = permissions.find((perm) => {
          const pid = perm?.id ?? perm?.permission_id ?? perm?.value;
          return pid === id;
        });
        const label = p?.name ?? p?.label ?? p?.codename ?? matched?.name ?? matched?.label ?? matched?.codename ?? String(id);
        return label.toLowerCase().includes(searchLower) || String(id).includes(searchLower);
      });
    }

    if (filteredPermList.length === 0) {
      return <div className="text-sm text-muted-foreground px-2 py-2">No permissions match your search</div>;
    }

    return (
      <div className="space-y-1">
        {filteredPermList.map((p) => {
          const id = p?.id ?? p?.permission_id ?? p?.value;
          const matched = permissions.find((perm) => {
            const pid = perm?.id ?? perm?.permission_id ?? perm?.value;
            return pid === id;
          });
          const label = p?.name ?? p?.label ?? p?.codename ?? matched?.name ?? matched?.label ?? matched?.codename ?? String(id);
          return (
            <div key={id} className="text-sm text-foreground px-2 py-1 border-b border-border last:border-b-0">
              {label}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Manage Roles</h1>
              <p className="text-muted-foreground mt-1">Create, edit, and remove roles.</p>
            </div>
            <button
              onClick={toggleCreate}
              className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
            >
              {isCreating ? 'Cancel' : 'Create New Role'}
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-md border border-red-300 bg-red-50 text-red-700">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {isCreating && (
            <form onSubmit={handleSubmit} className="mb-8 bg-card border border-border rounded-lg p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Role name</label>
                <input
                  type="text"
                  value={formValues.name}
                  onChange={(e) => setFormValues((v) => ({ ...v, name: e.target.value }))}
                  className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                  placeholder="e.g., Surveyor"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                <textarea
                  value={formValues.description}
                  onChange={(e) => setFormValues((v) => ({ ...v, description: e.target.value }))}
                  className="w-full border border-border rounded-md px-3 py-2 bg-background text-foreground"
                  rows={3}
                  placeholder="Describe the role"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-foreground">Permissions</label>
                  {isLoadingPermissions && (
                    <span className="text-xs text-muted-foreground">Loading permissions…</span>
                  )}
                </div>
                
                {/* Selected Permissions Section */}
                {formValues.permissions.length > 0 && (
                  <div className="mb-3 p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-md">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <label className="block text-sm sm:text-base font-medium text-foreground">
                        Selected Permissions
                      </label>
                      <span className="text-xs sm:text-sm font-semibold text-primary bg-primary/20 px-2 sm:px-3 py-1 rounded-full inline-block w-fit">
                        {formValues.permissions.length} {formValues.permissions.length === 1 ? 'permission' : 'permissions'}
                      </span>
                    </div>
                    <div className="max-h-32 sm:max-h-40 overflow-auto space-y-1 sm:space-y-2">
                      {formValues.permissions.map((permId) => {
                        const perm = permissions.find((p) => {
                          const id = p?.id ?? p?.permission_id ?? p?.value;
                          return id === permId;
                        });
                        const label = perm?.name ?? perm?.label ?? perm?.codename ?? String(permId);
                        return (
                          <div key={permId} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 text-sm bg-background px-2 sm:px-3 py-2 sm:py-1.5 rounded border border-border">
                            <span className="text-foreground break-words sm:break-normal">{label}</span>
                            <button
                              type="button"
                              onClick={() => handleCheckboxToggle(permId)}
                              className="text-red-600 hover:text-red-700 text-xs sm:text-sm px-2 py-1 sm:py-0.5 rounded hover:bg-red-50 transition-colors self-start sm:self-auto"
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Search input for permissions */}
                <div className="mb-2 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search permissions..."
                    value={permissionSearchCreate}
                    onChange={(e) => setPermissionSearchCreate(e.target.value)}
                    className="w-full pl-10 sm:pl-12 pr-4 py-2 sm:py-2.5 border border-border rounded-md bg-background text-foreground text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                {filteredPermissionsCreate.length > 0 && (
                  <label className="flex items-center gap-3 px-2 py-1 mb-1 cursor-pointer border-b border-border">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={filteredPermissionsCreate.every((p) => formValues.permissions.includes(p?.id ?? p?.permission_id ?? p?.value))}
                      onChange={(e) => {
                        const ids = filteredPermissionsCreate.map((p) => p?.id ?? p?.permission_id ?? p?.value);
                        setFormValues((prev) => ({
                          ...prev,
                          permissions: e.target.checked
                            ? [...new Set([...prev.permissions, ...ids])]
                            : prev.permissions.filter((id) => !ids.includes(id))
                        }));
                      }}
                    />
                    <span className="text-sm font-medium text-foreground">Select All</span>
                  </label>
                )}
                <div className="max-h-60 sm:max-h-72 overflow-auto border border-border rounded-md p-2 sm:p-3 bg-background">
                  {permissions?.length === 0 && !isLoadingPermissions && (
                    <div className="text-sm text-muted-foreground px-2 py-2">
                      No permissions found.
                      <button type="button" onClick={fetchPermissions} className="ml-2 underline">Retry</button>
                    </div>
                  )}
                  {filteredPermissionsCreate?.length === 0 && permissions?.length > 0 && (
                    <div className="text-sm text-muted-foreground px-2 py-2">No permissions match your search</div>
                  )}
                  {filteredPermissionsCreate?.map((perm) => {
                    const id = perm?.id ?? perm?.permission_id ?? perm?.value;
                    const label = perm?.name ?? perm?.label ?? perm?.codename ?? String(id);
                    const checked = formValues.permissions.includes(id);
                    return (
                      <label key={id} className="flex items-center gap-3 px-2 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={checked}
                          onChange={() => handleCheckboxToggle(id)}
                        />
                        <span className="text-sm text-foreground">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
                >
                  {isSubmitting ? 'Creating…' : 'Create Role'}
                </button>
                <button
                  type="button"
                  onClick={toggleCreate}
                  className="inline-flex items-center px-4 py-2 rounded-md border border-border bg-card text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">Role</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">Description</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-sm text-muted-foreground">No roles yet. Create your first role.</td>
                  </tr>
                ) : (
                  roles.map((role) => (
                    <tr key={role.id} className="border-t border-border">
                      <td className="px-4 py-3 text-foreground">{role.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{role.description}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openView(role)}
                            className="px-3 py-1 rounded-md border border-border text-sm"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => openEdit(role)}
                            className="px-3 py-1 rounded-md border border-border text-sm"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => requestDelete(role)}
                            className="px-3 py-1 rounded-md bg-red-600 text-white text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {viewingRole && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[1100] p-4">
              <div className="w-full max-w-lg bg-card border border-border rounded-lg p-4 sm:p-6">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-foreground">Role Details</h2>
                  {isLoadingView && (
                    <span className="text-xs text-muted-foreground">Loading…</span>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm text-muted-foreground">Name</div>
                    <div className="text-foreground font-medium">{viewDetails?.name || viewingRole?.name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Description</div>
                    <div className="text-foreground">{viewDetails?.description || viewingRole?.description || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Permissions</div>
                    {/* Search input for permissions in view mode */}
                    <div className="mb-2 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search permissions..."
                        value={permissionSearchView}
                        onChange={(e) => setPermissionSearchView(e.target.value)}
                        className="w-full pl-10 sm:pl-12 pr-4 py-2 sm:py-2.5 border border-border rounded-md bg-background text-foreground text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div className="max-h-60 sm:max-h-72 overflow-auto border border-border rounded-md p-2 sm:p-3 bg-background">
                      {renderViewPermissions()}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={closeView}
                      className="inline-flex items-center px-4 py-2 rounded-md border border-border bg-card text-sm font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {actionError && (
            <div className="mt-4 p-3 rounded-md border border-red-300 bg-red-50 text-red-700">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <p className="text-sm">{actionError}</p>
              </div>
            </div>
          )}

          {actionSuccess && (
            <div className="mt-4 p-3 rounded-md border border-green-300 bg-green-50 text-green-700">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <p className="text-sm">{actionSuccess}</p>
              </div>
            </div>
          )}

          {editingRole && (
            <div className="fixed inset-0 bg-black/30 flex items-start sm:items-center justify-center z-[1100] p-2 sm:p-4 overflow-y-auto">
              <div className="w-full max-w-lg bg-card border border-border rounded-lg p-4 sm:p-6 my-4 sm:my-8 max-h-[90vh] flex flex-col">
                <div className="mb-3 sm:mb-4 flex-shrink-0">
                  <h2 className="text-lg sm:text-xl font-semibold text-foreground">Edit Role</h2>
                </div>
                {actionSuccess && (
                  <div className="mb-3 sm:mb-4 p-3 rounded-md border border-green-300 bg-green-50 text-green-700 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      <p className="text-sm">{actionSuccess}</p>
                    </div>
                  </div>
                )}
                <form onSubmit={submitEdit} className="flex flex-col flex-1 min-h-0 space-y-4 sm:space-y-6 overflow-y-auto">
                  <div className="flex-shrink-0">
                    <label className="block text-sm sm:text-base font-medium text-foreground mb-1 sm:mb-2">Role name</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full border border-border rounded-md px-3 sm:px-4 py-2 sm:py-2.5 bg-background text-foreground text-sm sm:text-base"
                      required
                    />
                  </div>
                  <div className="flex-shrink-0">
                    <label className="block text-sm sm:text-base font-medium text-foreground mb-1 sm:mb-2">Description</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                      className="w-full border border-border rounded-md px-3 sm:px-4 py-2 sm:py-2.5 bg-background text-foreground text-sm sm:text-base resize-y"
                      rows={3}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm sm:text-base font-medium text-foreground">Permissions</label>
                      {(isLoadingPermissions || isLoadingEditPermissions) && (
                        <span className="text-xs sm:text-sm text-muted-foreground">Loading…</span>
                      )}
                    </div>
                    
                    {/* Selected Permissions Section */}
                    {editSelectedPermissions.length > 0 && (
                      <div className="mb-3 p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-md">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                          <label className="block text-sm sm:text-base font-medium text-foreground">
                            Selected Permissions
                          </label>
                          <span className="text-xs sm:text-sm font-semibold text-primary bg-primary/20 px-2 sm:px-3 py-1 rounded-full inline-block w-fit">
                            {editSelectedPermissions.length} {editSelectedPermissions.length === 1 ? 'permission' : 'permissions'}
                          </span>
                        </div>
                        <div className="max-h-32 sm:max-h-40 overflow-auto space-y-1 sm:space-y-2">
                          {editSelectedPermissions.map((permId) => {
                            const perm = permissions.find((p) => {
                              const id = p?.id ?? p?.permission_id ?? p?.value;
                              return id === permId;
                            });
                            const label = perm?.name ?? perm?.label ?? perm?.codename ?? String(permId);
                            return (
                              <div key={permId} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 text-sm bg-background px-2 sm:px-3 py-2 sm:py-1.5 rounded border border-border">
                                <span className="text-foreground break-words sm:break-normal">{label}</span>
                                <button
                                  type="button"
                                  onClick={() => toggleEditPermission(permId)}
                                  className="text-red-600 hover:text-red-700 text-xs sm:text-sm px-2 py-1 sm:py-0.5 rounded hover:bg-red-50 transition-colors self-start sm:self-auto"
                                >
                                  Remove
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Search input for permissions */}
                    <div className="mb-2 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search permissions..."
                        value={permissionSearchEdit}
                        onChange={(e) => setPermissionSearchEdit(e.target.value)}
                        className="w-full pl-10 sm:pl-12 pr-4 py-2 sm:py-2.5 border border-border rounded-md bg-background text-foreground text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    {filteredPermissionsEdit.length > 0 && (
                      <label className="flex items-center gap-3 px-2 py-1 mb-1 cursor-pointer border-b border-border">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={filteredPermissionsEdit.every((p) => editSelectedPermissions.includes(p?.id ?? p?.permission_id ?? p?.value))}
                          onChange={(e) => {
                            const ids = filteredPermissionsEdit.map((p) => p?.id ?? p?.permission_id ?? p?.value);
                            setEditSelectedPermissions((prev) =>
                              e.target.checked
                                ? [...new Set([...prev, ...ids])]
                                : prev.filter((id) => !ids.includes(id))
                            );
                          }}
                        />
                        <span className="text-sm font-medium text-foreground">Select All</span>
                      </label>
                    )}
                    <div className="max-h-60 sm:max-h-72 overflow-auto border border-border rounded-md p-2 sm:p-3 bg-background">
                      {permissions?.length === 0 && !isLoadingPermissions && (
                        <div className="text-sm text-muted-foreground px-2 py-2">No permissions found.</div>
                      )}
                      {filteredPermissionsEdit?.length === 0 && permissions?.length > 0 && (
                        <div className="text-sm text-muted-foreground px-2 py-2">No permissions match your search</div>
                      )}
                      {filteredPermissionsEdit?.map((perm) => {
                        const id = perm?.id ?? perm?.permission_id ?? perm?.value;
                        const label = perm?.name ?? perm?.label ?? perm?.codename ?? String(id);
                        const checked = editSelectedPermissions.includes(id);
                        return (
                          <label key={id} className="flex items-center gap-3 px-2 py-1 cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={checked}
                              onChange={() => toggleEditPermission(id)}
                            />
                            <span className="text-sm text-foreground">{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 pt-2 sm:pt-0 flex-shrink-0">
                    <button
                      type="submit"
                      disabled={isUpdatingRole}
                      className="inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-2.5 rounded-md bg-primary text-primary-foreground text-sm sm:text-base font-medium disabled:opacity-60 order-2 sm:order-1"
                    >
                      {isUpdatingRole ? 'Saving…' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={closeEdit}
                      className="inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-2.5 rounded-md border border-border bg-card text-sm sm:text-base font-medium order-1 sm:order-2"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {pendingDeleteRole && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[1100]">
              <div className="w-full max-w-md bg-card border border-border rounded-lg p-4">
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-foreground">Delete Role</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Are you sure you want to delete the role "{pendingDeleteRole?.name}"?
                  This action cannot be undone.
                </p>
                <div className="flex items-center gap-3 justify-end">
                  <button
                    type="button"
                    onClick={cancelDelete}
                    className="inline-flex items-center px-4 py-2 rounded-md border border-border bg-card text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDelete}
                    disabled={isDeleting}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium disabled:opacity-60"
                  >
                    {isDeleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ManageRoles;


